/**
 * Три действия у клиента — всегда те же и на тех же местах.
 *
 * Шаг 4 утверждённого флоу (`docs/superpowers/prototypes/2026-08-18-consultant-flow/`,
 * правка founder 18.08): «Завести хозяйство» · «Добавить поля» · «Показать
 * поля на карте». Один и тот же компонент в строке списка и в карточке
 * клиента — иначе два места разъезжаются, и человек, привыкший к строке,
 * заново ищет кнопки в карточке.
 *
 * Недоступное **гасим и объясняем**, а не прячем: пропадающие кнопки сдвигают
 * соседние, и каждую строку приходится перечитывать заново. Причина — в
 * подсказке, потому что «серая кнопка без объяснения» читается как поломка.
 */
import { MapPin, Plus, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { hasFarm, type ClientState } from '@/lib/consultant/types';

export interface ClientActionsProps {
  state: ClientState;
  /** Полей в кабинете — по нему включается «Показать на карте». */
  fieldsCount: number;
  /** Отдел хозяйства: попадает в подсказку «уже заведено — Отдел Юг». */
  clusterName?: string | null;
  onSetupFarm: () => void;
  onAddFields: () => void;
  onShowOnMap: () => void;
  busy?: boolean;
  /** Компактный вид для строки таблицы (в карточке кнопки крупнее). */
  dense?: boolean;
  /** Суффикс для data-testid — чтобы в таблице тесты адресовали строку. */
  idSuffix?: string;
}

/** Кнопка с подсказкой-причиной, когда она погашена. */
function ActionButton({
  label,
  icon,
  primary,
  disabledReason,
  onClick,
  busy,
  dense,
  testId,
}: {
  label: string;
  icon: React.ReactNode;
  primary: boolean;
  disabledReason: string | null;
  onClick: () => void;
  busy?: boolean;
  dense?: boolean;
  testId: string;
}) {
  const disabled = disabledReason !== null || !!busy;
  const button = (
    <Button
      size={dense ? 'sm' : 'default'}
      // Основное действие — зелёный primary Earth Tech (как в утверждённом
      // макете), остальные — outline. accent-brand (янтарный) держим для
      // акцентов состояния, а не для трёх кнопок в каждой строке таблицы:
      // пятьдесят янтарных строк подряд перестают что-либо выделять.
      variant={primary ? 'default' : 'outline'}
      disabled={disabled}
      aria-disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={cn(disabledReason !== null && 'pointer-events-none')}
    >
      {icon}
      {label}
    </Button>
  );
  if (disabledReason === null) return button;
  return (
    <Tooltip>
      {/* span-обёртка: у disabled-кнопки нет событий указателя, и подсказка
          без неё не всплывает — то есть причина остаётся ненаписанной. */}
      <TooltipTrigger asChild>
        <span tabIndex={0}>{button}</span>
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  );
}

export function ClientActions({
  state,
  fieldsCount,
  clusterName,
  onSetupFarm,
  onAddFields,
  onShowOnMap,
  busy,
  dense = false,
  idSuffix = '',
}: ClientActionsProps) {
  const farmed = hasFarm(state);
  const suffix = idSuffix ? `-${idSuffix}` : '';
  const iconCls = dense ? 'mr-2 h-3.5 w-3.5' : 'mr-2 h-4 w-4';

  // «Уже заведено» без имени отдела — половина ответа: человек всё равно
  // пойдёт открывать карточку, чтобы узнать, куда клиент попал.
  const setupReason = farmed
    ? clusterName
      ? `Уже заведено — ${clusterName}`
      : 'Хозяйство уже заведено'
    : null;
  const mapReason = fieldsCount > 0 ? null : 'Полей пока нет';

  return (
    // Свой TooltipProvider: компонент стоит и в таблице списка, и в шапке
    // карточки, а причина погашенной кнопки не должна зависеть от того,
    // догадался ли экран обернуть себя провайдером. Вложенный провайдер для
    // Radix допустим.
    <TooltipProvider>
      <div className={cn('flex flex-wrap items-center gap-2')}>
        <ActionButton
          label='Завести хозяйство'
          icon={<Warehouse className={iconCls} />}
          primary={!farmed}
          disabledReason={setupReason}
          onClick={onSetupFarm}
          busy={busy}
          dense={dense}
          testId={`action-setup-farm${suffix}`}
        />
        <ActionButton
          label='Добавить поля'
          icon={<Plus className={iconCls} />}
          // Основное действие ровно у заведённого пустого хозяйства: там это
          // единственный осмысленный следующий шаг.
          primary={state === 'farm_ready'}
          disabledReason={null}
          onClick={onAddFields}
          busy={busy}
          dense={dense}
          testId={`action-add-fields${suffix}`}
        />
        <ActionButton
          label='Показать на карте'
          icon={<MapPin className={iconCls} />}
          primary={fieldsCount > 0}
          disabledReason={mapReason}
          onClick={onShowOnMap}
          busy={busy}
          dense={dense}
          testId={`action-show-map${suffix}`}
        />
      </div>
    </TooltipProvider>
  );
}
